<template>
  <div class="containerSlideEditor" :ref="'containerSlideEditor'" :class="classesTemplate">
    <div class="coloredBackground" :class="classesTemplate" :style="'border-bottom: 4px solid '+theme ">

      <!-- VIDEO BACKGROUND -->
      <VideoImageGenerator v-if="isVideoBackground && !proceedImage"  :url="slideVideoImageUrl||slideImageUrl"
                           :orientation="'landscape'"
                            :mask="maskForImage" @imageGenerated="onImageGenerated"
                           @videoGenerated="onVideoGenerated"
                           :prompt="currentSlide?.promptIllustrationGeneration"/>



      <!-- TITLE -->
      <div class="titleElement"  :class="classesTemplate"
           >
        <div v-html="slideTitle" ref="title-container" :contenteditable="isEditable"
             :placeholder="$t('aiAvatar.slideTemplate.titlePlaceholder')" class="titleTxt"
        :style="'color:'+theme+'; font-size:'+titleFontSize+'px;' "
             @focusout="validateTitle"/>
      </div>

      <!-- DESCRIPTION -->
      <div class="descriptionElement" :class="classesTemplate">
        <div v-html="slideDescription" ref="description-container"
             :style="'border-left: 1px solid '+theme+'; font-size:'+descriptionFontSize+'px;'"
             :placeholder="$t('aiAvatar.slideTemplate.descriptionPlaceholder')"
             :contenteditable="isEditable" class="descriptionTxt"
             @focusout="validateDescription"/>
      </div>
    </div>

    <div class="avatarLayout" :class="classesTemplate">
      <div class="avatarBackground" :style="'background-image:url('+backgroundSelected?.url+')'">
      </div>
      <VideoAvatarPreview />
    </div>

    <div class="videoLayout" :class="classesTemplate">
      <VideoImageGenerator v-if="isVideoLayoutBackground  && !proceedImage"  :url="slideVideoImageUrl||slideImageUrl"
                           :orientation="'portrait'" @imageGenerated="onImageGenerated"
                           @videoGenerated="onVideoGenerated"
                           :mask="'none'" :prompt="currentSlide?.promptIllustrationGeneration"/>
    </div>
    <div class="logoElement" :class="classesTemplate">
      <img class="logoWorkspace" :src="logoSlide"/>
    </div>
  </div>

</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import store from "@/store";
import { AvatarStyle, LIST_OF_BACKGROUND } from "@/store/AIAvatar/aiavatarGenerationModel";
import { getImageURLForSize, htmliFy } from "@/utils/util";
import { txtColorForBackground } from "@/utils/color";
import { clone } from "@/utils/cloneUtil";
import { Slide, SlideTemplate } from "@/store/slides2Video/slides2VideoModels";
import { updateCurrentSlide } from "@/utils/slides/slideUtil";
import VideoImageBackground from "@/components/videoImageBackground/VideoImageBackground.vue";
import VideoImageGenerator from "@/components/videoImageBackground/VideoImageGenerator.vue";
import { DESCRIPTION_FONT_RATIO, TITLE_FONT_RATIO } from "@/components/recorder/aiAvatar/panel/AISlideConstant";
import { isSpeach } from "@/utils/speachUtils";
import VideoAvatarPreview from "@/components/videoImageBackground/VideoAvatarPreview.vue";



@Component({
  components: { VideoAvatarPreview, VideoImageGenerator, VideoImageBackground }
})
export default class AISlidePanel extends Vue {

  @Prop({ default: false })
  private shouldEditable!: boolean;

  private slideContainerWidth:number=0;
  private resizeObserverSlideContainer!: ResizeObserver;

  private isEditable=false;

  private proceedImage=false;

  get isVideoBackground(){
    return this.slideTemplate===SlideTemplate.TITLE_ILLUSTRATED || this.slideTemplate===SlideTemplate.ILLUSTRATED_ONLY;
  }
  get maskForImage(){
    return this.slideTemplate===SlideTemplate.TITLE_ILLUSTRATED?'light':'';
  }
  get isVideoLayoutBackground(){
    return this.slideTemplate===SlideTemplate.ILLUSTRATED_RIGHT || this.slideTemplate===SlideTemplate.ILLUSTRATED_LEFT ;
  }

  get classesTemplate() {
    return this.slideTemplate;
  }

  get slideTitle() {
    return htmliFy(this.currentSlide?.slideTitle ||"");
  }

  get slideDescription() {
    const customTag={'div':[],'p':[],'span':[],'pre':[]};
    return htmliFy(this.currentSlide?.slideDescription ||"",customTag);
  }

  get slideTemplate() {
    return this.currentSlide?.slideTemplate || SlideTemplate.AVATAR_LEFT;
  }

  get theme() {
    return this.currentSlide?.slideTheme || "#7957D5";
  }

  get slideImageUrl(){
    if(this.currentSlide?.slideImageB64){
      return `${this.currentSlide.slideImageB64}`;
    }else{
      return ""
    }
  }
  get slideVideoImageUrl(){
    if(this.currentSlide?.slideVideoUrl){
      return `${this.currentSlide.slideVideoUrl}`;
    }else{
      return ""
    }
  }

  get txtColor(){
    return txtColorForBackground(this.theme);
  }

  get avatarSelected() {
    return store.getters.getAvatarSelected;
  }

  get backgroundSelected() {
    return LIST_OF_BACKGROUND.find((b) => {
      return store.getters.getAvatarBabckgroundSelected === b.background;
    });
  }

  get currentSlide(): Slide | null {
    if (store.getters.getCurrentSlideIndex > -1) {
      return store.getters.slides[store.getters.getCurrentSlideIndex];
    }
    return null;
  }

  get titleFontSize(){
    return this.slideContainerWidth/TITLE_FONT_RATIO;
  }
  get descriptionFontSize(){
    return this.slideContainerWidth/DESCRIPTION_FONT_RATIO;
  }

  get logoSlide(){
    if(isSpeach()){
      return  store.getters.getSpeachLogo;
    }else {
      return getImageURLForSize(store.getters.getSelectedWorkspace?.imageURL, 160);
    }
  }

  mounted() {
    this.isEditable=this.shouldEditable;

    this.initObserver()
  }

  private initObserver(){
    const el = this.$refs["containerSlideEditor"] as HTMLDivElement;
    // Nettoyage observer
    if (this.resizeObserverSlideContainer) {
      this.resizeObserverSlideContainer.disconnect();
    }
    this.resizeObserverSlideContainer = new ResizeObserver(entries => {
      for (const entry of entries) {
        this.slideContainerWidth = entry.contentRect.width;
      }
    });
    this.resizeObserverSlideContainer.observe(el);
  }

  private async validateTitle(event: Event) {
    if (event.target != null) {
      const element = event.target as HTMLElement;
      if (element.innerText.trim() !== this.slideTitle.trim()) {
        const newSlide=clone(this.currentSlide) as Slide;
        newSlide.slideTitle=element.innerText.trim();
        updateCurrentSlide(newSlide)

      }
    }
  }

  private async validateDescription(event: Event) {
    if (event.target != null) {
      const element = event.target as HTMLElement;
      const customTag={'div':[],'p':[],'span':[],'pre':[]};
      if (htmliFy(element.innerHTML.trim()) !== this.slideDescription.trim()) {
        const newSlide=clone(this.currentSlide) as Slide;
        newSlide.slideDescription=htmliFy(element.innerHTML.trim(),customTag);
        updateCurrentSlide(newSlide)
      }
    }
  }

  private async onImageGenerated({prompt,image}:{prompt:string,image:string}) {
    const newSlide=clone(this.currentSlide) as Slide;
    newSlide.slideImageB64=image;
    newSlide.promptIllustrationGeneration=prompt;
    updateCurrentSlide(newSlide)
    this.proceedImage=true;
    setTimeout(()=>{
      this.proceedImage=false;
    },500)
  }
  private async onVideoGenerated({prompt,video}:{prompt:string,video:string}){
    const newSlide=clone(this.currentSlide) as Slide;
    newSlide.promptIllustrationGeneration=prompt;
    newSlide.slideVideoUrl=video;
    updateCurrentSlide(newSlide)
    this.proceedImage=true;
    setTimeout(()=>{
      this.proceedImage=false;
    },500)
  }

  @Watch('slideTemplate')
  async onSlideTemplateChange(){
    this.initObserver()
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide) as Slide;
      updateCurrentSlide(newSlide)
    }
  }


  beforeDestroy() {
    // Nettoyage observer
    if (this.resizeObserverSlideContainer) {
      this.resizeObserverSlideContainer.disconnect();
    }
  }

}
</script>

<style lang="scss" scoped>
.containerSlideEditor {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
  color: white;

  .coloredBackground{
    width: 100%;
    height: 100%;
    position: absolute;
    top:0;
    right:0;
    display: flex;
    background: white;
    flex-direction: column;
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.4);
  }

  .logoElement{
    position: absolute;
    bottom: 12px;
    right: 16px;
    width: 120px;
    height: 32px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    .logoWorkspace{
      height: 100%;
      object-fit: contain;
      margin-left: auto;
    }
  }
  .titleElement {
    position: relative;
    font-weight: 700;
    max-height: 33%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 4%;
    margin-bottom: 4%;
    .titleTxt{
      margin-left: 4%;
      margin-right: 4%;
      padding-left: 4%;
      padding-right: 4%;
      font-weight: 900;
      cursor: text;
      &[placeholder]:empty::before {
        content: attr(placeholder);
        color: var(--light1);
      }

      &[placeholder]:empty:focus::before {
        content: "";
      }
      &:focus{
        box-shadow: inset 0px 0px 0px 1px var(--light1);
      }
    }
  }
  .descriptionElement {
    position: relative;
    margin-top: 0;
    margin-bottom: 4%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    max-height: 67%;
    color: var(--black);
    .descriptionTxt{
      margin-left: 4%;
      margin-right: 4%;
      padding-left: 4%;
      padding-right: 4%;
      text-align: left;
      cursor: text;
      ::v-deep strong,::v-deep b{
        color: inherit;
        font-weight: 900;
      }
      ::v-deep pre{
        background: transparent;
        font-family: inherit;
        font-size: inherit;
      }
      &[placeholder]:empty::before {
        content: attr(placeholder);
        color: var(--light1);
      }

      &[placeholder]:empty:focus::before {
        content: "";
      }
      &:focus{
        box-shadow: inset 0px 0px 0px 1px var(--light1);
      }
    }
  }

  .avatarLayout{
    position: absolute;
    top:0;
    width: 40%;
    height: 100%;
    display: none;
    &.AVATAR_RIGHT{
      display: block;
    }
    &.AVATAR_LEFT{
      left: 0;
      display: block;
    }
    .avatarBackground{
      position: absolute;
      width: 100%;
      left: 0px;
      height: 100%;
      background-size: cover;
    }

  }

  .videoLayout{
    position: absolute;
    top:0;
    height: 100%;
    display: none;
    &.ILLUSTRATED_LEFT{
      display: block;
      width: 40%;
      left: 0px;
    }
    &.ILLUSTRATED_RIGHT{
      display: block;
      width: 40%;
      right: 0px;
    }
    &.ILLUSTRATED_ONLY{
      display: block;
      width: 100%;
      left: 0px;
    }
  }


  // TEMPLATE AND THEME DEFINITION
  .coloredBackground{
    &.AVATAR_LEFT, &.ILLUSTRATED_LEFT{
      width: 60%;
      right:0;
    }
    &.AVATAR_RIGHT, &.ILLUSTRATED_RIGHT{
      width: 60%;
      left:0;
    }
  }

  .avatarLayout{
    &.AVATAR_LEFT{
      left: 0;
      .avatarBackground{
      }
    }
    &.AVATAR_RIGHT{
      right: 0;
      .avatarBackground{
      }
    }
  }
  .titleElement {
    &.ILLUSTRATED_ONLY{
      display:none;
    }
  }

  .descriptionElement {
    &.ILLUSTRATED_ONLY{
      display:none;
    }
  }

}
</style>
